'use client';

import {Guide} from '@blesk-frontend/api';
import {ErrorBoundary} from '@sentry/react';
import {Pill} from '../../components';
import {formatLongDate} from '../../libs';
import {formatImageToInternalUrl} from './formatImageToInternalUrl';
import {StepErrorComponent, StepRenderer} from './index';
import {textWithLink} from './textWithLink';

interface GuideViewProps {
  guide: Guide;
  print?: boolean;
  pdf?: boolean;
  s3?: {
    AWS_S3_PUBLIC_ENDPOINT: string | null;
    AWS_S3_INTERNAL_PUBLIC_ENDPOINT: string | null;
  };
}

export const GuideView = ({guide, print = false, pdf = false, s3}: GuideViewProps) => {
  return (
    <div className="prose prose-sm md:prose-base mx-auto max-w-screen-md outline-0">
      <div className="mb-4 flex items-center gap-4 md:mb-6">
        <Pill>{guide.user.fullName}</Pill>
        <div className="text-paragraph-sm text-text-secondary">
          {formatLongDate(guide.createdAt)}
        </div>
      </div>

      <h1 className="mb-4 text-text-primary md:mb-6">{guide.name}</h1>
      <p className="mb-4 whitespace-pre-line text-text-secondary leading-[18px] md:mb-6 md:leading-5">
        {guide.description}
      </p>

      {guide.steps
        .filter((step) => !step.payload.meta.hidden)
        .sort((a, b) => a.position - b.position)
        .map((step, index) => {
          const formattedStep = {
            ...step.payload,
            src: {
              ...step.payload.src,
              original:
                formatImageToInternalUrl(step.payload.src.original, {
                  s3PublicEndpoint: s3?.AWS_S3_PUBLIC_ENDPOINT || null,
                  s3InternalPublicEndpoint: s3?.AWS_S3_INTERNAL_PUBLIC_ENDPOINT || null,
                }) || '',
              edited: formatImageToInternalUrl(step.payload.src.edited, {
                s3PublicEndpoint: s3?.AWS_S3_PUBLIC_ENDPOINT || null,
                s3InternalPublicEndpoint: s3?.AWS_S3_INTERNAL_PUBLIC_ENDPOINT || null,
              }),
              cropped: formatImageToInternalUrl(step.payload.src.cropped || null, {
                s3PublicEndpoint: s3?.AWS_S3_PUBLIC_ENDPOINT || null,
                s3InternalPublicEndpoint: s3?.AWS_S3_INTERNAL_PUBLIC_ENDPOINT || null,
              }),
            },
          };

          return (
            <ErrorBoundary
              key={`step-${step.id}`}
              fallback={<StepErrorComponent index={index + 1} />}
            >
              <StepRenderer
                key={`step-${step.id}`}
                index={index + 1}
                step={formattedStep}
                print={print}
                pdf={pdf}
                zoom={false}
                caption={
                  <div className="tiptap-inline-prose prose prose-sm md:prose-base w-full max-w-full whitespace-pre-line break-words leading-5">
                    {index === 0
                      ? textWithLink(step.payload.element.text)
                      : step.payload.element.text}
                  </div>
                }
              />
            </ErrorBoundary>
          );
        })}
    </div>
  );
};
